import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import {DeleteOutline, DoneAll, MarkEmailReadOutlined, Visibility, VisibilityOff} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../core/constants/enums';
import {ContactUsMessagesDataGridColumnNames} from '../../../../../../core/constants/table/data-grid-column-keys';
import DateUtils from '../../../../../../core/services/date-utils';
import BroadcastService from '../../../../../../core/services/broadcast';
import Api from '../../../../../../core/services/api/api';
import PartnerDataGrid from '../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState,} from '../../../../../../packages/data-grid';
import useSearch from '../../../../../hooks/use-search';
import useIsMounted from '../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../hooks/use-access-levels';
import IconButton from '../../../../../components/base/button/icon_button';
import SearchInput from '../../../../../components/base/input/search_input';
import {ListPageShell, PageActions, SearchContainer} from '../../../../../components/page-shell';
import MessagePeeksHost from './message-peeks-host';

const exportingChunkSize = 100;
const maxOpenIds = 6;
const fallbackPeekOrigin = {x: 48, y: 88};
const dataGridSaveConfig = {name: 'ContactUsMessagesDataGrid', version: 5};
const col = ContactUsMessagesDataGridColumnNames;

/**
 * Peek origin just to the right of the actions cell (not the eye icon), or the fallback cascade origin.
 * @param {MouseEvent|undefined} event
 * @return {{x: number, y: number}}
 */
const getPeekOriginFromEvent = (event) => {
    const target = event?.currentTarget ?? event?.target;
    const anchor = typeof target?.closest === 'function'
        ? (target.closest('td') ?? target.closest('[role="cell"]') ?? target)
        : target;
    const rect = typeof anchor?.getBoundingClientRect === 'function'
        ? anchor.getBoundingClientRect()
        : null;
    if (!rect) {
        return fallbackPeekOrigin;
    }
    return {x: rect.right + 16, y: rect.top};
};

/**
 * Publishes that the Contact Us unread count may have changed (sidebar badge re-fetches).
 */
const notifyContactUsUnreadCountChanged = () => {
    BroadcastService.postMessage(BroadcastService.channelNames.contactUsMessagesUnreadCount, null);
};

/**
 * Formats a submit datetime for display in the grid and exports.
 */
const formatSubmitDateTime = (value) => {
    if (!value) {
        return '';
    }
    return DateUtils.serverToLocal(value).format(DateUtils.dateTimeFormat);
};

/**
 * Builds the list request body for ContactMessage/List.
 * @param {{filters?: object, orderBy?: {field?: string, descending?: boolean}, paginationInfo?: object}} args
 * @param {{isView?: boolean|null}} [extra]
 */
const buildListRequest = (args, extra = {}) => {
    const sorts = args.orderBy?.field
        ? [{property: args.orderBy.field, isDesc: !!args.orderBy.descending}]
        : [];
    /** @type {Record<string, unknown>} */
    const body = {
        keyword: args.filters?.keyword?.toString()?.trim() || null,
        pagination: {
            currentPage: args.paginationInfo?.currentPage ?? 1,
            pageSize: args.paginationInfo?.pageSize ?? DefaultPaginationInfo.pageSize,
        },
        sorts,
    };
    if (extra.isView === true || extra.isView === false) {
        body.isView = extra.isView;
    }
    return body;
};

/**
 * Support list of Contact Us form submissions.
 * Full-width grid + multi-open draggable message peeks.
 */
const ContactUsMessagesView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.support.contactUsMessages'});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportContactUsMessages = operatorAccessLevels.packages.support.exportContactUsMessages;
    const {configureNotebookTags} = useConfigureNotebook('support-contact-us-messages');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [data, setData] = useState([]);
    /** @type {[Record<string|number, object>, React.Dispatch<React.SetStateAction<Record<string|number, object>>>]} */
    const [messagesById, setMessagesById] = useState({});
    /** @type {[(string|number)[], React.Dispatch<React.SetStateAction<(string|number)[]>>]} */
    const [openIds, setOpenIds] = useState([]);
    /** @type {[string|number|null, React.Dispatch<React.SetStateAction<string|number|null>>]} */
    const [focusedId, setFocusedId] = useState(null);
    /** @type {[Record<string, {x: number, y: number}>, React.Dispatch<React.SetStateAction<Record<string, {x: number, y: number}>>>]} */
    const [peekOrigins, setPeekOrigins] = useState({});
    const [actionLoading, setActionLoading] = useState(false);

    /**
     * Configures notebook tags and behavioral analytics for this page.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.supportContactUsMessages);
        configureBehavioralTool((tags) => tags.supportContactUsMessages);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Drops open peeks for messages no longer in the cache.
     */
    useEffect(() => {
        setOpenIds((prev) => {
            const nextOpen = prev.filter((id) => messagesById[id] != null);
            setFocusedId((current) => {
                if (current != null && nextOpen.includes(current)) {
                    return current;
                }
                return nextOpen[nextOpen.length - 1] ?? null;
            });
            return nextOpen.length === prev.length ? prev : nextOpen;
        });
    }, [messagesById]);

    /**
     * Drops stored peek origins for ids that are no longer open.
     */
    useEffect(() => {
        setPeekOrigins((prev) => {
            const kept = new Set(openIds.map(String));
            let removed = false;
            const next = {};
            Object.keys(prev).forEach((key) => {
                if (kept.has(key)) {
                    next[key] = prev[key];
                } else {
                    removed = true;
                }
            });
            return removed ? next : prev;
        });
    }, [openIds]);

    /**
     * Merges list page items into the by-id cache used by peeks.
     * @param {Array<object>} items
     */
    const mergeMessagesCache = useCallback((items) => {
        if (!items?.length) {
            return;
        }
        setMessagesById((prev) => {
            const next = {...prev};
            items.forEach((item) => {
                if (item?.id != null) {
                    next[item.id] = item;
                }
            });
            return next;
        });
    }, []);

    const columns = useMemo(() => [
        {
            title: t('columns.name'),
            name: col.name,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.subject'),
            name: col.subject,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.phone'),
            name: col.phone,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: 140,
        },
        {
            title: t('columns.email'),
            name: col.email,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.submitDateTime'),
            name: col.submitDateTime,
            type: DataGridColumnTypes.dateTime,
            alignment: DataGridColumnAlignments.left,
            width: 180,
        },
        {
            title: t('columns.isRead'),
            name: col.isRead,
            type: DataGridColumnTypes.boolean,
            alignment: DataGridColumnAlignments.center,
            width: 90,
        },
        {
            title: t('columns.actions'),
            name: col.actions,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            pinned: true,
            pinnedToggleable: false,
            visibilityToggleable: false,
            width: 70,
        },
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => (
        dataGridPageSize
            ? {...DefaultPaginationInfo, pageSize: dataGridPageSize}
            : DefaultPaginationInfo
    ), [dataGridPageSize]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }), [dataGridSortBy, initialPaginationInfo]);

    /**
     * Loads a page of messages. Pass forExport to return data without updating table state.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const response = await Api.listContactMessages(buildListRequest(args));
        if (!isMounted()) {
            return;
        }
        if (!response?.resultFlag) {
            return forExport ? response : undefined;
        }
        const items = response.data?.items ?? [];
        const totalCount = response.data?.totalCount ?? 0;
        if (!forExport) {
            setData(items);
            mergeMessagesCache(items);
            args.setPaginationInfo?.((prev) => ({
                ...prev,
                length: totalCount,
            }));
        }
        if (forExport) {
            return {resultFlag: true, data: {items, totalCount}};
        }
    }, [isMounted, mergeMessagesCache]);

    /**
     * useSearch entry point used on page filter/sort/pagination changes.
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const searchData = useSearch(search, initialSearchDataSettings);
    const {performSearch} = searchData;

    /**
     * Applies keyword filter via useSearch.
     */
    const searchByKeyword = useCallback((keywords) => {
        searchData.setFilters((prevState) => ({
            ...prevState,
            keyword: keywords?.toString()?.trim() ?? '',
        }));
    }, [searchData]);

    /**
     * Maps message records to export columns.
     */
    const formatExportingData = useCallback((list) => ({
        title: t('exportTitle'),
        body: list?.map((item) => ({
            [t('columns.name')]: item.name ?? '',
            [t('columns.subject')]: item.subject ?? '',
            [t('columns.phone')]: item.phone ?? '',
            [t('columns.email')]: item.email ?? '',
            [t('columns.submitDateTime')]: formatSubmitDateTime(item.submitDateTime),
            [t('columns.isRead')]: item.isView ? t('yes') : t('no'),
            [t('columns.message')]: item.message ?? '',
        })) ?? [],
    }), [t]);

    /**
     * Exports all filtered messages using chunked page fetches.
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted()) {
                return;
            }
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData]);

    /**
     * Opens a draggable peek for the message (or focuses an already-open peek).
     * @param {string|number} id
     * @param {MouseEvent} [event]
     */
    const openMessage = useCallback((id, event) => {
        setOpenIds((prev) => {
            if (prev.includes(id)) {
                return prev;
            }
            const next = [...prev, id];
            if (next.length > maxOpenIds) {
                return next.slice(next.length - maxOpenIds);
            }
            return next;
        });
        setPeekOrigins((prev) => {
            if (prev[id] != null || prev[String(id)] != null) {
                return prev;
            }
            return {
                ...prev,
                [id]: getPeekOriginFromEvent(event),
            };
        });
        setFocusedId(id);
    }, []);

    /**
     * Closes a single peek without affecting others; focuses the last remaining peek when needed.
     */
    const closeMessage = useCallback((id) => {
        if (id == null) {
            return;
        }
        setOpenIds((prev) => {
            const next = prev.filter((openId) => openId !== id);
            setFocusedId((current) => {
                if (current != null && current !== id && next.includes(current)) {
                    return current;
                }
                return next[next.length - 1] ?? null;
            });
            return next;
        });
    }, []);

    /**
     * Optimistically patches isView on current page data and peek cache.
     */
    const patchMessageView = useCallback((id, isView) => {
        setData((prev) => prev.map((item) => (
            item.id === id ? {...item, isView} : item
        )));
        setMessagesById((prev) => {
            const existing = prev[id];
            if (!existing) {
                return prev;
            }
            return {...prev, [id]: {...existing, isView}};
        });
    }, []);

    /**
     * Marks a single message as viewed (read).
     */
    const markAsRead = useCallback(async (id) => {
        setActionLoading(true);
        const response = await Api.setContactMessageView({id, isView: true});
        if (!isMounted()) {
            return;
        }
        setActionLoading(false);
        if (!response?.resultFlag) {
            return;
        }
        patchMessageView(id, true);
        notifyContactUsUnreadCountChanged();
    }, [isMounted, patchMessageView, t]);

    /**
     * Removes a message and closes its peek if open.
     */
    const removeMessage = useCallback(async (id) => {
        setActionLoading(true);
        const response = await Api.deleteContactMessage(id);
        if (!isMounted()) {
            return;
        }
        setActionLoading(false);
        if (!response?.resultFlag) {
            return;
        }
        setOpenIds((prev) => prev.filter((openId) => openId !== id));
        setFocusedId((current) => (current === id ? null : current));
        setMessagesById((prev) => {
            if (prev[id] == null) {
                return prev;
            }
            const next = {...prev};
            delete next[id];
            return next;
        });
        notifyContactUsUnreadCountChanged();
        await performSearch();
    }, [isMounted, performSearch, t]);

    /**
     * Confirms then removes one message.
     */
    const confirmRemoveMessage = useCallback((item) => {
        window.partner?.openConfirmationDialog(
            <Box sx={{p: 2}}>
                <Typography variant="body2" color="text.secondary">
                    {t('confirmations.removeMessage', {name: item.name, subject: item.subject})}
                </Typography>
            </Box>,
            {maxWidth: 'sm'},
            (confirmed) => confirmed && removeMessage(item.id),
        );
    }, [removeMessage, t]);

    /**
     * Fetches all matching message ids (optionally only unread) via paginated List.
     * @param {{isView?: boolean|null}} filter
     * @returns {Promise<Array<object>>}
     */
    const fetchAllMessages = useCallback(async (filter = {}) => {
        const pageSize = exportingChunkSize;
        let currentPage = 1;
        let totalCount = Infinity;
        const items = [];
        while (items.length < totalCount) {
            const response = await Api.listContactMessages(buildListRequest({
                filters: searchData.filters,
                orderBy: searchData.orderBy,
                paginationInfo: {currentPage, pageSize},
            }, filter));
            if (!response?.resultFlag) {
                return null;
            }
            const pageItems = response.data?.items ?? [];
            totalCount = response.data?.totalCount ?? pageItems.length;
            items.push(...pageItems);
            if (!pageItems.length) {
                break;
            }
            currentPage++;
        }
        return items;
    }, [searchData.filters, searchData.orderBy]);

    /**
     * Marks every unread message as viewed.
     */
    const markAllAsRead = useCallback(async () => {
        setActionLoading(true);
        const messages = await fetchAllMessages({isView: false});
        if (!isMounted()) {
            return;
        }
        if (!messages) {
            setActionLoading(false);
            return;
        }
        for (const item of messages) {
            if (item.isView) {
                continue;
            }
            const response = await Api.setContactMessageView({id: item.id, isView: true});
            if (!isMounted()) {
                return;
            }
            if (!response?.resultFlag) {
                setActionLoading(false);
                return;
            }
        }
        setActionLoading(false);
        notifyContactUsUnreadCountChanged();
        await performSearch();
    }, [fetchAllMessages, isMounted, performSearch, t]);

    /**
     * Confirms then marks all unread messages as read.
     */
    const confirmMarkAllAsRead = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <Box sx={{p: 2}}>
                <Typography variant="body2" color="text.secondary">
                    {t('confirmations.markAllAsRead')}
                </Typography>
            </Box>,
            {maxWidth: 'sm'},
            (confirmed) => confirmed && markAllAsRead(),
        );
    }, [markAllAsRead, t]);

    /**
     * Soft-deletes every matching message.
     */
    const removeAll = useCallback(async () => {
        setActionLoading(true);
        const messages = await fetchAllMessages({});
        if (!isMounted()) {
            return;
        }
        if (!messages) {
            setActionLoading(false);
            return;
        }
        for (const item of messages) {
            const response = await Api.deleteContactMessage(item.id);
            if (!isMounted()) {
                return;
            }
            if (!response?.resultFlag) {
                setActionLoading(false);
                return;
            }
        }
        setOpenIds([]);
        setFocusedId(null);
        setMessagesById({});
        setActionLoading(false);
        notifyContactUsUnreadCountChanged();
        await performSearch();
    }, [fetchAllMessages, isMounted, performSearch, t]);

    /**
     * Confirms then removes all messages.
     */
    const confirmRemoveAll = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <Box sx={{p: 2}}>
                <Typography variant="body2" color="text.secondary">
                    {t('confirmations.removeAll')}
                </Typography>
            </Box>,
            {maxWidth: 'sm'},
            (confirmed) => confirmed && removeAll(),
        );
    }, [removeAll, t]);

    /**
     * Resolves a message for a peek from the cache (survives pagination).
     */
    const resolveMessage = useCallback((id) => (
        messagesById[id] ?? null
    ), [messagesById]);

    const hasRows = (searchData.paginationInfo?.length ?? 0) > 0 || data.length > 0;
    const busy = searchData.loading || actionLoading;

    const pageActionItems = useMemo(() => [
        {
            id: 'mark-all-as-read',
            label: t('actions.markAllAsRead'),
            icon: <DoneAll/>,
            onClick: confirmMarkAllAsRead,
            buttonProps: {
                disabled: !hasRows || busy,
                color: 'primary',
                size: 'medium',
            },
        },
        {
            id: 'remove-all',
            label: t('actions.removeAll'),
            icon: <DeleteOutline/>,
            onClick: confirmRemoveAll,
            buttonProps: {
                disabled: !hasRows || busy,
                color: 'error',
                size: 'medium',
            },
        },
    ], [busy, confirmMarkAllAsRead, confirmRemoveAll, hasRows, t]);

    const rows = useMemo(() => data.map((row) => {
        const isOpened = openIds.includes(row.id);
        const isViewed = !!row.isView;
        const classNames = isViewed
            ? 'contact-us-message-row--read'
            : 'contact-us-message-row--unread';
        const openActionLabel = isOpened ? t('actions.close') : t('actions.open');

        return {
            key: row.id,
            data: row,
            className: classNames,
            cells: {
                [col.name]: row.name,
                [col.subject]: row.subject,
                [col.phone]: row.phone,
                [col.email]: row.email,
                [col.submitDateTime]: row.submitDateTime,
                [col.isRead]: isViewed,
                [col.actions]: (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title={openActionLabel}>
                            <IconButton
                                color="paper.primary"
                                size="medium"
                                aria-label={openActionLabel}
                                onPressed={(e) => (
                                    isOpened ? closeMessage(row.id) : openMessage(row.id, e)
                                )}
                            >
                                {isOpened
                                    ? <VisibilityOff className="icon medium"/>
                                    : <Visibility className="icon medium"/>}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('actions.markAsRead')}>
                            <span>
                                <IconButton
                                    color="paper.secondary"
                                    size="medium"
                                    aria-label={t('actions.markAsRead')}
                                    disabled={isViewed || busy}
                                    onPressed={() => markAsRead(row.id)}
                                >
                                    <MarkEmailReadOutlined className="icon medium"/>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('actions.remove')}>
                            <IconButton
                                color="paper.error"
                                size="medium"
                                aria-label={t('actions.remove')}
                                disabled={busy}
                                onPressed={() => confirmRemoveMessage(row)}
                            >
                                <DeleteOutline className="icon medium"/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        };
    }), [busy, closeMessage, confirmRemoveMessage, data, markAsRead, openIds, openMessage, t]);

    return (
        <ListPageShell
            title={t('title')}
            subtitle={t('subtitle')}
            actions={<PageActions items={pageActionItems}/>}
            filters={(
                <SearchContainer>
                    <SearchInput
                        loading={busy}
                        autoSearch
                        immediateSearch
                        search={searchByKeyword}
                        initialValue={searchData.filters?.keyword ?? undefined}
                        label={t('searchLabel')}
                    />
                </SearchContainer>
            )}
        >
            <Box
                sx={{
                    width: 1,
                    '& .contact-us-message-row--unread td': {
                        fontWeight: 600,
                        bgcolor: 'action.hover',
                    },
                    '& .contact-us-message-row--read td': {
                        color: 'text.secondary',
                    },
                }}
            >
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{columns}}
                    columns={dataGridColumns}
                    rows={rows}
                    hideFooterActions
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: busy},
                        density: dataGridDensity,
                    }}
                    getExportInfo={canExportContactUsMessages ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo((prev) => ({
                        ...prev,
                        ...current,
                    }))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo((prev) => ({
                        ...prev,
                        ...current,
                    }))}
                />
            </Box>

            <MessagePeeksHost
                openIds={openIds}
                focusedId={focusedId}
                origins={peekOrigins}
                resolveMessage={resolveMessage}
                loading={busy}
                onFocus={setFocusedId}
                onClose={closeMessage}
                onMarkAsRead={markAsRead}
                onRemove={confirmRemoveMessage}
            />
        </ListPageShell>
    );
};

export default ContactUsMessagesView;
